import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { orderBy, deburr, toLower } from "lodash";
import { setCurrentUserId } from "../../redux/slices/UserSlice";
import { changeUserTeam } from "../../redux/slices/SeasonSlice";
import { sendPushNotification, resetNotificationState } from "../../redux/slices/NotificationSlice";
import userAdmin from "../../assets/images/user-admin.svg";
import PageLoadingFailNotification from "../PageLoadingFailNotification/PageLoadingFailNotification";
import TablePagination from "./table-pagination/TablePagination";
import Enums from "../../assets/JsonData/Enums";
import useUserRole from "../../hooks/useUserRole";

const UsersTable = ({ filteredUsersInfos }) => {
  const { usersInfos: allUsersInfos, refreshUserPage } = useSelector((state) => ({ ...state.userInfos }));
  const { seasonInfos } = useSelector((state) => ({ ...state.seasonList }));
  const { leagueConfigs } = useSelector((state) => ({ ...state.leagueConfig }));
  const { loadingPush, errorPush, successPush } = useSelector(state => state.notifications);
  const { isAdmin, isManager, hasAccessAsDeveloper } = useUserRole();
  const { accounts } = useMsal();

  // On utilise filteredUsersInfos pour l'affichage du tableau,
  // mais allUsersInfos pour résoudre les noms (managers, équipes, etc.)
  const usersInfos = filteredUsersInfos ?? allUsersInfos;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState("");

  const [notifModalOpen, setNotifModalOpen] = useState(false);
  const [notifTitle, setNotifTitle] = useState("");
  const [notifContent, setNotifContent] = useState("");

  const teamsList = seasonInfos?.data?.seasonTeams;

  useEffect(() => {
    if (successPush && notifModalOpen) {
      setTimeout(() => {
        closeNotifModal();
        dispatch(resetNotificationState());
      }, 1500);
    }
  }, [successPush, notifModalOpen, dispatch]);

  const getLeagueTitleFromLeagueId = (leagueId) => {
    if (!leagueConfigs?.data || leagueConfigs.data.length === 0) return "vide";
    const result = leagueConfigs.data.find((league) => league.leagueId === leagueId);
    return result === undefined ? " " : result.name;
  };

  const getTeamNameFromTeamId = (teamId) => {
    if (!seasonInfos?.data) return "";
    const team = seasonInfos.data.seasonTeams.find((item) => item.teamId === teamId);
    return team === undefined ? "" : team.name;
  };

  // Pour résoudre les noms de managers on utilise toujours allUsersInfos
  const getManagerNameById = (managerId) => {
    if (!allUsersInfos?.data || !managerId) return "";
    const manager = allUsersInfos.data.find((user) => user.userId === managerId);
    return manager ? `${manager.firstName} ${manager.lastName}` : "";
  };

  const getManagersList = useCallback(() => {
    if (!usersInfos?.data) return [];
    const managerIds = Array.from(
      new Set(usersInfos.data.map((u) => u.operatingManagerId).filter(Boolean))
    );
    return (allUsersInfos?.data ?? []).filter((u) => managerIds.includes(u.userId));
  }, [usersInfos, allUsersInfos]);

  const managerDisplayValueFunc = useCallback((manager) => {
    if (!manager) return "-";
    if (typeof manager === "object" && manager.firstName) {
      return `${manager.firstName} ${manager.lastName}`;
    }
    const found = allUsersInfos?.data?.find((u) => u.userId === manager);
    return found ? `${found.firstName} ${found.lastName}` : "-";
  }, [allUsersInfos]);

  const getCompaniesList = useCallback(() => {
    return [
      { value: 0, label: "Diggers" },
      { value: 1, label: "Finaura" }
    ];
  }, []);

  const companyDisplayValueFunc = useCallback((company) => {
    if (company == null) return "-";
    if (typeof company === "object" && company.label) return company.label;
    return Enums.matchCompany(company);
  }, []);

  const openModal = (user) => {
    setSelectedUser(user);
    setSelectedTeam(user.teamId ?? "");
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
    setSelectedTeam("");
  };

  const confirmChangeTeam = () => {
    if (selectedUser && selectedTeam !== "") {
      dispatch(changeUserTeam({ userId: selectedUser.userId, teamId: selectedTeam }));
    }
    closeModal();
  };

  const openNotifModal = (user) => {
    setSelectedUser(user);
    setNotifTitle("");
    setNotifContent("");
    setNotifModalOpen(true);
  };
  const closeNotifModal = () => {
    setNotifModalOpen(false);
    setSelectedUser(null);
  };

  const handleSendNotification = (title, content) => {
    dispatch(sendPushNotification({
      userIds: [selectedUser.userId],
      push: { title: title.trim(), body: content.trim() },
      redirection: 0,
      parentId: "",
      pushType: 0,
    }));
  };

  const columns = [
    {
      Header: "Prénom & Nom",
      accessor: (row) => row.firstName + " " + row.lastName,
      Cell: ({ value, row }) => (
        <span>
          {value} {row.original.canChangeTeamDuringSeason && (
            <img
              src={userAdmin}
              alt="AM"
              width={14}
              style={{ verticalAlign: "baseline" }}
            />
          )}
        </span>
      ),
      sortType: (rowA, rowB, columnId) => {
        const nameA = rowA.values[columnId].toLowerCase();
        const nameB = rowB.values[columnId].toLowerCase();
        return nameA.localeCompare(nameB);
      },
    },
    {
      Header: "Role",
      accessor: "role",
      Cell: ({ row }) => Enums.matchClass(row.original.role) + (row.original.isGhost ? " (ghost)" : ""),
    },
    {
      Header: "Société",
      accessor: "company",
      Cell: ({ value }) => Enums.matchCompany(value),
    },
    {
      Header: "Mng. Op.",
      accessor: "operatingManagerId",
      Cell: ({ value }) => {
        const managerName = getManagerNameById(value);
        return managerName !== "" ? managerName : "-";
      },
    },
    {
      Header: "Niveau",
      accessor: "experience",
      Cell: ({ value }) => value.levelId,
      sortType: (rowA, rowB, columnId) => {
        const levelA = rowA.values[columnId].levelId;
        const levelB = rowB.values[columnId].levelId;
        return levelA - levelB;
      },
    },
    {
      Header: "Ligue",
      accessor: "leagueId",
      Cell: ({ value }) => getLeagueTitleFromLeagueId(value),
    },
    {
      Header: "Boost total (%)",
      accessor: "totalBoost",
      Cell: ({ value }) => value ?? "",
    },
    {
      Header: "Equipe",
      accessor: "teamId",
      Cell: ({ value }) => getTeamNameFromTeamId(value),
    },
    {
      Header: "Cristaux",
      accessor: "score",
    },
    {
      Header: "Or",
      accessor: "coins",
    },
    {
      Header: "Activé",
      accessor: "isActivated",
      Cell: ({ value }) => (value === true ? "Oui" : "Non"),
      sortType: "basic",
    },
  ];

  // Colonne changement équipe uniquement pour les admins
  if (isAdmin || hasAccessAsDeveloper) {
    columns.push({
      Header: "Réassigner équipe",
      accessor: "assignTeam",
      Cell: ({ row }) => {
        if (row.original.canChangeTeamDuringSeason) {
          return (
            <button onClick={() => openModal(row.original)} className="btn btn-sm btn-primary">
              Changer équipe
            </button>
          );
        }
        return null;
      },
      disableSortBy: true,
    });
  }

  // Colonne notification uniquement pour les admins
  if (isAdmin || hasAccessAsDeveloper) {
    columns.push({
      Header: "Notification",
      accessor: "notification",
      Cell: ({ row }) => (
        <button
          onClick={() => openNotifModal(row.original)}
          className="btn btn-sm btn-outline-secondary"
          title={`Envoyer une notification à ${row.original.firstName} ${row.original.lastName}`}
          style={{ cursor: "pointer" }}
        >
          <i className="bx bx-bell"></i>
        </button>
      ),
      disableSortBy: true,
    });
  }

  /* eslint @typescript-eslint/no-empty-function: "off", @typescript-eslint/no-unused-vars: "off" */
  const tableHooks = (hooks) => {};

  const handleRowClicked = (row) => {
    dispatch(setCurrentUserId(row.original));
    navigate("/UserDetailledInfosPage/" + row.original.userId, { state: { currentUserInfos: row.original } });
  };

  const getOrderedData = useCallback(() => {
    return orderBy(usersInfos.data, [(user) => deburr(toLower(user.firstName))], ["asc"]);
  }, [usersInfos]);

  const TeamChangeModal = () => {
    if (!isModalOpen || !selectedUser) return null;

    const userId = selectedUser.userId;
    const availableTeams =
      teamsList?.filter(team =>
        !(Array.isArray(team.users) && team.users.includes(userId))
      ) ?? [];

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h5>Changer équipe pour {selectedUser.firstName} {selectedUser.lastName}</h5>
          <select
            value={selectedTeam}
            onChange={e => setSelectedTeam(e.target.value)}
            style={{ width: "100%", margin: "15px 0", padding: 5 }}
          >
            <option value="">-- Choisir une équipe --</option>
            {availableTeams.map(team => (
              <option key={team.teamId} value={team.teamId}>
                {team.name}
              </option>
            ))}
          </select>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={closeModal} className="btn btn-secondary">Annuler</button>
            <button onClick={confirmChangeTeam} className="btn btn-primary" disabled={selectedTeam === ""}>Valider</button>
          </div>
        </div>
      </div>
    );
  };

  const NotificationModal = () => {
    const titleRef = useRef(null);
    const contentRef = useRef(null);

    if (!notifModalOpen) return null;

    const user = selectedUser || { firstName: "", lastName: "" };

    const onSendClick = () => {
      const title = titleRef.current?.value || "";
      const content = contentRef.current?.value || "";
      if (!title.trim() || !content.trim()) {
        alert("Le titre et le contenu sont obligatoires");
        return;
      }
      handleSendNotification(title, content);
    };

    return (
      <div
        className="modal-overlay"
        style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <div
          className="modal-content"
          style={{
            backgroundColor: "white",
            borderRadius: 8,
            width: 360,
            maxWidth: "90vw",
            padding: 24,
            boxShadow: "0 2px 15px rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <h3 style={{ margin: 0 }}>
            Envoyer une notification à {user.firstName} {user.lastName}
          </h3>

          <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: 6, fontWeight: "600" }}>Titre</label>
            <input
              type="text"
              maxLength={100}
              placeholder="Titre de la notification"
              ref={titleRef}
              style={{
                padding: "8px 10px",
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 14,
                width: "100%",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div className="form-group" style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: 6, fontWeight: "600" }}>Contenu</label>
            <textarea
              maxLength={300}
              placeholder="Contenu de la notification"
              ref={contentRef}
              style={{
                padding: "8px 10px",
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 14,
                resize: "vertical",
                minHeight: 100,
                maxHeight: 250,
                width: "100%",
                boxSizing: "border-box",
                overflowWrap: "break-word",
                whiteSpace: "pre-wrap",
              }}
            />
          </div>

          {(errorPush || successPush) && (
            <div style={{ marginTop: 4, color: errorPush ? "red" : "green" }}>
              {errorPush || "Notification envoyée avec succès !"}
            </div>
          )}

          <div
            className="modal-actions"
            style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 14 }}
          >
            <button
              className="btn btn-secondary"
              onClick={closeNotifModal}
              style={{ padding: "8px 18px", cursor: "pointer" }}
              type="button"
            >
              Annuler
            </button>
            <button
              className="btn btn-primary"
              onClick={onSendClick}
              disabled={loadingPush}
              style={{ padding: "8px 18px", cursor: loadingPush ? "not-allowed" : "pointer" }}
              type="button"
            >
              {loadingPush ? "Envoi en cours..." : "Envoyer"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {usersInfos && usersInfos.status === 200 && usersInfos.data ? (
        <>
          <TablePagination
            data={getOrderedData() ?? []}
            columns={columns}
            tableHooks={tableHooks}
            refresh={refreshUserPage}
            handleRowClicked={handleRowClicked}
            type={"default"}
            enableFilter={true}
            hasDefaultValue={false}
          >
            <element
              key="operatingManagerId"
              filterKey="operatingManagerId"
              className="cell"
              casesensitive={"true"}
              showsearch={"true"}
              data={getManagersList()}
              itemDisplayValueFunc={managerDisplayValueFunc}
            >
              Manager
            </element>
            <element
              key="company"
              filterKey="company"
              className="cell"
              casesensitive={"true"}
              showsearch={"true"}
              data={getCompaniesList()}
              itemDisplayValueFunc={companyDisplayValueFunc}
            >
              Société
            </element>
          </TablePagination>
          {(isAdmin || hasAccessAsDeveloper) && <TeamChangeModal />}
          {(isAdmin || hasAccessAsDeveloper) && <NotificationModal />}
        </>
      ) : (
        <PageLoadingFailNotification text={"La répuration des données a échoué !"} />
      )}
    </div>
  );
};

export default UsersTable;
